import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { fetchHomepage, submitGeneralEnquiry } from "../api/marketingApi";
import { trackVisit, getVisitorId } from "../hooks/useVisitor";
import { trackContactWhatsApp, trackContactPhone, trackLead } from "../lib/metaPixel";
import logoUrl from "src/assets/property-makers-logo.png";

// The shared marketing chrome: sticky translucent nav + footer, in the
// off-white/black brand. Children render between them.
export function MarketingShell({ children }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  // Contact details for the footer come from the configurable homepage content.
  const [contact, setContact] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchHomepage().then((d) => alive && setContact(d?.contact || null)).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Anonymous page-view beacon on every route change EXCEPT listing pages —
  // those track themselves with the listing token (see MarketingListing).
  useEffect(() => {
    if (!location.pathname.startsWith("/p/")) trackVisit();
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#F4F2ED] text-[#14130F] antialiased [font-family:'Iowan_Old_Style','Palatino_Linotype',Palatino,Georgia,serif]">
      <header className="sticky top-0 z-30 border-b border-[#DED9CC] bg-[#F4F2ED]/85 backdrop-blur-md backdrop-saturate-150">
        <div className="mx-auto flex max-w-[74rem] items-center gap-4 px-6" style={{ height: 92 }}>
          <Link to="/" className="flex items-center" aria-label="PropertyMakers home">
            <img
              src={logoUrl}
              alt="Property Makers"
              className="w-auto"
              style={{ height: 68 }}
            />
          </Link>

          <nav className="ml-auto hidden items-center gap-7 font-sans text-[0.92rem] font-medium sm:flex">
            <Link to="/properties" className="text-[#4A473E] hover:text-[#14130F]">Properties</Link>
            <Link to="/about" className="text-[#4A473E] hover:text-[#14130F]">About</Link>
            <a href="/#contact" className="text-[#4A473E] hover:text-[#14130F]">Contact</a>
            <Link to="/properties" className="rounded-[9px] bg-[#2C4A87] px-4 py-2 font-semibold text-white hover:bg-[#1F3766]">
              Browse listings
            </Link>
          </nav>

          <button
            type="button"
            className="ml-auto sm:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {open && (
          <div className="border-t border-[#DED9CC] px-6 py-4 font-sans sm:hidden">
            <Link to="/properties" onClick={() => setOpen(false)} className="block py-2 text-[#4A473E]">Properties</Link>
            <Link to="/about" onClick={() => setOpen(false)} className="block py-2 text-[#4A473E]">About</Link>
            <Link to="/properties" onClick={() => setOpen(false)} className="mt-2 inline-block rounded-lg bg-[#2C4A87] px-4 py-2 font-semibold text-white">
              Browse listings
            </Link>
          </div>
        )}
      </header>

      {/* Extra bottom room on mobile so the sticky CTA bar never covers content. */}
      <main className="pb-20 sm:pb-0">{children}</main>

      <footer className="mt-8 bg-[#14130F] py-12 text-[#E9E4D6]">
        <div className="mx-auto max-w-[74rem] px-6">
          <div className="flex flex-wrap justify-between gap-8 font-sans">
            <div className="max-w-[22rem]">
              <div className="text-[1.2rem] font-bold">
                Property<span className="text-[#CBB07B]">Makers</span>
              </div>
              <p className="mt-1.5 text-[0.82rem] text-[#948E7E]">
                Your Dream Property Partner. Handpicked residential, commercial and NA-plot properties, with guidance you can trust.
              </p>
            </div>
            <div className="text-[0.9rem]">
              <FooterLink to="/properties">All properties</FooterLink>
              <FooterLink to="/properties?state=new">New properties</FooterLink>
              <FooterLink to="/properties?state=resale">Resale</FooterLink>
              <FooterLink to="/properties?state=rental">Rentals</FooterLink>
            </div>
            <div className="text-[0.9rem]">
              <FooterLink to="/about">About us</FooterLink>
              <FooterLink to="/about#work">Previous work</FooterLink>
              <a href="/#contact" className="mb-2 block text-[#C9C3B4] hover:text-white">Contact</a>
            </div>

            {/* Contact details (from configurable homepage content) */}
            {contact && (
              <div className="max-w-[18rem] space-y-1.5 text-[0.85rem] text-[#C9C3B4]">
                {contact.address && <div>{contact.address}</div>}
                {contact.phone && (
                  <div><a href={`tel:${contact.phone}`} onClick={() => trackContactPhone()} className="hover:text-white">{contact.phone}</a></div>
                )}
                {contact.email && (
                  <div><a href={`mailto:${contact.email}`} className="hover:text-white">{contact.email}</a></div>
                )}
                {contact.hours && <div className="text-[#948E7E]">{contact.hours}</div>}
                <SocialIcons contact={contact} />
              </div>
            )}
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-[#2C2A22] pt-5 font-sans text-[0.8rem] text-[#7C7768]">
            <span>© {new Date().getFullYear()} PropertyMakers. All rights reserved.</span>
            <Link to="/privacypolicy" className="hover:text-white">Privacy Policy</Link>
          </div>
        </div>
      </footer>

      <MobileContactBar contact={contact} />
      <TimedQuotePopup />
    </div>
  );
}

function FooterLink({ to, children }) {
  return (
    <Link to={to} className="mb-2 block text-[#C9C3B4] hover:text-white">
      {children}
    </Link>
  );
}

// Brand navy — shared by both CTA buttons and the Get-quote form.
const CTA_BLUE = "#2C4A87";

// A sticky bottom action bar shown only on mobile, always visible so a visitor
// can call us or request a quote from any marketing page. "Get Quote" opens a
// quick name + phone lead form. Renders only when a phone is configured.
function MobileContactBar({ contact }) {
  const [quoteOpen, setQuoteOpen] = useState(false);
  if (!contact) return null;
  const phone = String(contact.phone || "").trim();
  if (!phone) return null;

  return (
    <>
      <div className="fixed inset-x-0 bottom-0 z-40 flex font-sans shadow-[0_-2px_12px_rgba(20,19,15,0.15)] sm:hidden">
        <a
          href={`tel:${phone}`}
          onClick={() => trackContactPhone()}
          className="flex flex-1 items-center justify-center gap-2 border-r border-white/25 px-4 py-3.5 text-[0.95rem] font-semibold text-white active:opacity-90"
          style={{ backgroundColor: CTA_BLUE }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M6.62 10.79a15.15 15.15 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24 11.36 11.36 0 003.56.57 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1 11.36 11.36 0 00.57 3.56 1 1 0 01-.25 1.01l-2.2 2.22z"/></svg>
          Call Now
        </a>
        <button
          type="button"
          onClick={() => setQuoteOpen(true)}
          className="flex flex-1 items-center justify-center gap-2 px-4 py-3.5 text-[0.95rem] font-semibold text-white active:opacity-90"
          style={{ backgroundColor: CTA_BLUE }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 5h16M4 12h16M4 19h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
          Get Quote
        </button>
      </div>
      {quoteOpen && <QuoteModal onClose={() => setQuoteOpen(false)} />}
    </>
  );
}

// Site-wide "Get a quote" lead form (name + phone). Posts a general website lead
// via submitGeneralEnquiry, then shows a thank-you state. The header copy is
// customisable so the timed pop-up can use a more eye-catching message.
function QuoteModal({
  onClose,
  onSubmitted,
  eyebrow = "Get a quote",
  title = "Tell us how to reach you",
  subcopy = "Share your name and number — we’ll call you back with a quote.",
}) {
  const [form, setForm] = useState({ name: "", phone: "", consent: true, company: "" });
  const [state, setState] = useState("idle"); // idle | sending | done | error
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) return setError("Please enter your name.");
    if (!/^[6-9]\d{9}$/.test(form.phone.trim())) return setError("Enter a valid 10-digit mobile number.");
    setError("");
    setState("sending");
    try {
      // When we're on a listing page (/p/:token), tie the lead to that listing.
      const m = typeof window !== "undefined" && window.location.pathname.match(/^\/p\/([^/]+)/);
      const res = await submitGeneralEnquiry({
        name: form.name.trim(),
        phone: form.phone.trim(),
        consent: form.consent,
        company: form.company, // honeypot
        visitor_id: getVisitorId(),
        listing_token: m ? m[1] : undefined,
      });
      if (res.ok) { setState("done"); trackLead({ content_name: "Get a quote" }); onSubmitted?.(); }
      else { setState("error"); setError((res.errors || ["Something went wrong."]).join(" ")); }
    } catch (err) {
      setState("error");
      setError(err?.response?.data?.errors?.join(" ") || "Network error. Please try again.");
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 font-sans sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full rounded-t-2xl bg-[#FBFAF6] p-6 shadow-[0_20px_60px_rgba(20,19,15,0.35)] sm:max-w-[420px] sm:rounded-2xl"
      >
        <button type="button" onClick={onClose} className="absolute right-3 top-3 text-[#78746A] hover:text-[#14130F]" aria-label="Close">
          <X className="h-5 w-5" />
        </button>

        {state === "done" ? (
          <div className="py-4 text-center">
            <div className="text-[1.3rem] font-bold text-[#14130F]">Thank you!</div>
            <p className="mt-2 text-[0.92rem] text-[#4A473E]">Our team will call you back shortly with a quote.</p>
          </div>
        ) : (
          <>
            <div className="text-[0.72rem] font-bold uppercase tracking-[0.16em] text-[#B7975A]">{eyebrow}</div>
            <h3 className="mt-2 text-[1.4rem] font-bold leading-tight text-[#14130F]">
              {title}
            </h3>
            <p className="mt-1.5 text-[0.9rem] text-[#4A473E]">{subcopy}</p>

            {error && (
              <div className="mt-3 rounded-lg border border-[#9E4B34]/30 bg-[#9E4B34]/10 p-2.5 text-[0.85rem] text-[#9E4B34]">{error}</div>
            )}

            <form onSubmit={submit} className="mt-4 space-y-3">
              {/* Honeypot */}
              <div className="absolute left-[-9999px] h-0 overflow-hidden" aria-hidden="true">
                <input type="text" tabIndex={-1} autoComplete="off" value={form.company} onChange={set("company")} />
              </div>
              <input
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Your name"
                className="w-full rounded-[10px] border border-[#C9C3B4] bg-white px-[0.85rem] py-[0.7rem] text-[1rem] outline-none focus:border-[#2C4A87] focus:ring-2 focus:ring-[#2C4A87]/30"
              />
              <input
                required
                type="tel"
                inputMode="numeric"
                pattern="[6-9][0-9]{9}"
                maxLength={10}
                value={form.phone}
                onChange={set("phone")}
                placeholder="10-digit mobile number"
                className="w-full rounded-[10px] border border-[#C9C3B4] bg-white px-[0.85rem] py-[0.7rem] text-[1rem] outline-none focus:border-[#2C4A87] focus:ring-2 focus:ring-[#2C4A87]/30"
              />
              <label className="flex items-start gap-2.5 text-[0.8rem] text-[#4A473E]">
                <input type="checkbox" required checked={form.consent} onChange={set("consent")} className="mt-0.5" />
                <span>I agree to be contacted about properties via call, WhatsApp, SMS or email.</span>
              </label>
              <button
                type="submit"
                disabled={state === "sending"}
                className="w-full rounded-[10px] px-4 py-3 text-[1rem] font-semibold text-white disabled:opacity-60"
                style={{ backgroundColor: CTA_BLUE }}
              >
                {state === "sending" ? "Sending…" : "Get quote"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// A scroll-triggered "Get a quote" pop-up: opens once the visitor scrolls past
// 30% of the page, and again at 80% if they closed the first one without
// submitting. Each depth fires at most once. Stops permanently after a submit
// (remembered per browser). Scroll depth catches engaged readers better than a
// fixed timer.
const TQ_DONE_KEY = "pm_quote_submitted";
const TQ_DEPTHS = [0.3, 0.8]; // fire at 30% then 80% scroll

function TimedQuotePopup() {
  const [open, setOpen] = useState(false);
  // How many depth thresholds we've already triggered (0, 1, or 2).
  const stageRef = useRef(0);
  // Track open state in a ref so the scroll handler can read it without re-binding.
  const openRef = useRef(false);
  openRef.current = open;

  useEffect(() => {
    let submitted = false;
    try { submitted = localStorage.getItem(TQ_DONE_KEY) === "1"; } catch { /* ignore */ }
    if (submitted) return;

    const scrollFraction = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      if (max <= 0) return 0;
      return (window.scrollY || doc.scrollTop || 0) / max;
    };

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        try { if (localStorage.getItem(TQ_DONE_KEY) === "1") { detach(); return; } } catch { /* ignore */ }
        // Don't stack: skip while a popup is already open.
        if (openRef.current) return;
        const next = TQ_DEPTHS[stageRef.current];
        if (next != null && scrollFraction() >= next) {
          stageRef.current += 1;
          setOpen(true);
        }
      });
    };

    function detach() { window.removeEventListener("scroll", onScroll); }
    window.addEventListener("scroll", onScroll, { passive: true });
    // Fire once on mount too (in case the page loads already scrolled).
    onScroll();
    return detach;
  }, []);

  const handleClose = () => setOpen(false);

  const handleSubmitted = () => {
    stageRef.current = TQ_DEPTHS.length; // no more stages
    try { localStorage.setItem(TQ_DONE_KEY, "1"); } catch { /* ignore */ }
  };

  if (!open) return null;
  return (
    <QuoteModal
      onClose={handleClose}
      onSubmitted={handleSubmitted}
      eyebrow="Looking for the right property?"
      title="Get a free quote in minutes"
      subcopy="Leave your name and number — our Kalyan property experts will call you back with the best options and pricing."
    />
  );
}

// A row of brand social icons in the footer, each shown only when its URL is set
// in the homepage editor. WhatsApp is built from the contact WhatsApp number.
function SocialIcons({ contact }) {
  const waNumber = String(contact.whatsapp || "").replace(/[^\d]/g, "");
  const items = [
    waNumber && { key: "wa", label: "WhatsApp", href: `https://wa.me/${waNumber}`, icon: <WhatsAppGlyph />, onClick: trackContactWhatsApp },
    contact.facebook_url && { key: "fb", label: "Facebook", href: contact.facebook_url, icon: <FacebookGlyph /> },
    contact.instagram_url && { key: "ig", label: "Instagram", href: contact.instagram_url, icon: <InstagramGlyph /> },
    contact.youtube_url && { key: "yt", label: "YouTube", href: contact.youtube_url, icon: <YouTubeGlyph /> },
    contact.google_business_url && { key: "gmb", label: "Google Business", href: contact.google_business_url, icon: <GoogleBusinessGlyph /> },
  ].filter(Boolean);

  if (items.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2.5 pt-2">
      {items.map((it) => (
        <a
          key={it.key}
          href={it.href}
          target="_blank"
          rel="noreferrer"
          aria-label={it.label}
          title={it.label}
          onClick={it.onClick}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
        >
          {it.icon}
        </a>
      ))}
    </div>
  );
}

/* Brand glyphs (inline SVG, currentColor). Sized 18px for the footer circles. */
function WhatsAppGlyph() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884a9.82 9.82 0 016.988 2.898 9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884"/></svg>;
}
function FacebookGlyph() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073Z"/></svg>;
}
function InstagramGlyph() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069ZM12 0C8.741 0 8.332.014 7.052.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.332 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0Zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324ZM12 16a4 4 0 110-8 4 4 0 010 8Zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881Z"/></svg>;
}
function YouTubeGlyph() {
  // Slightly smaller than the others — the YouTube path fills its viewBox edge to
  // edge, so at 18px it looks cropped inside the circle. 15px matches the visual
  // padding of the Instagram/Facebook glyphs.
  return <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>;
}
function GoogleBusinessGlyph() {
  // A map-pin (Google Business / Maps listing).
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1112 6.5a2.5 2.5 0 010 5z"/></svg>;
}
